import { type QueryFunctionContext } from "@tanstack/react-query";
import config from "./config";
import {
  resolveForthLeafIdentity,
  type ForthLeafIdentity,
  type ForthLeafIdentityClaims,
} from "./forthLeafIdentity";
import {
  acquireForthLeafAccessToken,
  createForthLeafServiceClient,
  getForthLeafServiceResponseStatus,
} from "./forthLeafServiceRequest";

export type { ForthLeafIdentity, ForthLeafIdentityClaims };
export { resolveForthLeafIdentity };

interface UserPermissionsResponse {
  permissions: Array<{
    name: string;
    status: string;
  }>;
}

const permissionStaleTimeMs = 60 * 1000;

export const getForthLeafPermissionResponseStatus = getForthLeafServiceResponseStatus;
export const createForthLeafPermissionsQueryKey = (
  identity: ForthLeafIdentity | undefined,
): PermissionsQueryKey => [
  "forthLeafPermissions",
  identity?.subject ?? null,
  identity?.email ?? null,
];

export const fetchActiveForthLeafPermissions = async ({
  identity,
  signal,
  token,
}: {
  identity: ForthLeafIdentity;
  signal: AbortSignal;
  token: string;
}) => {
  const response = await createForthLeafServiceClient(
    config.usersServiceUrl,
    token,
  ).get<UserPermissionsResponse>(`user/${encodeURIComponent(identity.email)}`, { signal });

  return response.data.permissions
    .filter((permission) => permission.status === "active")
    .map((permission) => permission.name);
};

export type PermissionsQueryKey = readonly ["forthLeafPermissions", string | null, string | null];

export const createForthLeafPermissionsQuery = ({
  getAccessTokenSilently,
  identity,
}: {
  getAccessTokenSilently: () => Promise<string>;
  identity: ForthLeafIdentity | undefined;
}) => {
  const queryKey = createForthLeafPermissionsQueryKey(identity);

  return {
    enabled: Boolean(identity),
    queryKey,
    queryFn: async ({ signal }: QueryFunctionContext<PermissionsQueryKey>) => {
      if (!identity) {
        throw new Error("Forth Leaf identity is unavailable.");
      }

      const token = await acquireForthLeafAccessToken(getAccessTokenSilently, signal);

      return fetchActiveForthLeafPermissions({
        identity,
        signal,
        token,
      });
    },
    retry: false,
    staleTime: permissionStaleTimeMs,
  };
};
